import { isIcon, isPaint, isPaintCallback } from '@nextgis/paint';
import { featureFilter, isPropertyFilter } from '@nextgis/properties-filter';
import { Popup } from 'maplibre-gl';

import { convertMapClickEvent } from '../utils/convertMapClickEvent';
import { convertZoomLevel } from '../utils/convertZoomLevel';
import { allowedByType, typeAliasForFilter } from '../utils/geomType';
import { getCentroid } from '../utils/getCentroid';
import { createFeaturePositionOptions } from '../utils/getFeaturePosition';
import { getImage } from '../utils/imageIcons';
import { makeHtmlFromString } from '../utils/makeHtmlFromString';

import { BaseAdapter } from './BaseAdapter';

import type { IconPaint, Paint, PathPaint } from '@nextgis/paint';
import type {
  Operation,
  PropertiesFilter,
  PropertyFilter,
} from '@nextgis/properties-filter';
import type {
  DataLayerFilter,
  FilterOptions,
  LayerDefinition,
  OnLayerSelectType,
  PopupOnCloseFunction,
  PopupOptions,
  VectorAdapterLayerType,
  VectorAdapterOptions,
  VectorLayerAdapter,
} from '@nextgis/webmap';
import type {
  Feature as F,
  GeoJsonProperties,
  Geometry,
  GeometryObject,
} from 'geojson';
import type {
  FillLayerSpecification,
  FilterSpecification,
  LayerSpecification,
  LegacyFilterSpecification,
  LngLatLike,
  Map,
  MapEventType,
  MapGeoJSONFeature,
  MapLayerMouseEvent,
  MapMouseEvent,
  PopupOptions as MaplibrePopupOptions,
  SourceSpecification,
} from 'maplibre-gl';

import type {
  SelectedFeaturesIds,
  VectorLayerSpecification,
} from '../interfaces';
import type { TLayer } from '../MaplibreGLMapAdapter';

type Layer = VectorLayerSpecification;
type Layout = FillLayerSpecification['layout'];

interface OpenedPopup {
  feature: Feature;
  popup: Popup;
  closeHandlers: PopupOnCloseFunction[];
}

interface OpenedLabel {
  feature: Feature;
  popup: Popup;
}

export const operationsAliases: { [key in Operation]: string } = {
  gt: '>',
  lt: '<',
  ge: '>=',
  le: '<=',
  eq: '==',
  ne: '!=',
  in: 'in',
  notin: '!in',
  // NOT SUPPORTED
  like: '==',
  // NOT SUPPORTED
  ilike: '==',
};

const reversOperations: { [key in Operation]: string } = {
  gt: operationsAliases.le,
  lt: operationsAliases.ge,
  ge: operationsAliases.lt,
  le: operationsAliases.gt,
  eq: operationsAliases.ne,
  ne: operationsAliases.eq,
  in: operationsAliases.notin,
  notin: operationsAliases.in,
  like: operationsAliases.ne,
  ilike: operationsAliases.ne,
};

export interface EventOptions {
  silent?: boolean;
}

export interface Feature<
  G extends GeometryObject | null = Geometry,
  P = GeoJsonProperties,
> extends F<G, P> {
  _featureFilterId?: string;
}

const PAINT = {
  color: 'blue',
  opacity: 1,
  radius: 10,
};

type MaplibreGLLayerType = 'fill' | 'line' | 'symbol' | 'circle';

const maplibreGLTypeAlias: Record<VectorAdapterLayerType, MaplibreGLLayerType> =
  {
    polygon: 'fill',
    line: 'line',
    point: 'circle',
  };

export abstract class VectorAdapter<
  O extends VectorAdapterOptions = VectorAdapterOptions,
>
  extends BaseAdapter<O>
  implements VectorLayerAdapter<Map, TLayer, O, Feature>
{
  selected = false;
  map?: Map;
  protected featureIdName = '$id';
  protected _types: VectorAdapterLayerType[] = ['polygon', 'point', 'line'];
  protected readonly _sourceId: string;
  protected readonly _selectionName: string;
  protected _selectedFeatureIds: SelectedFeaturesIds | false = [];

  protected _selectProperties?: PropertiesFilter;
  protected _filterProperties?: PropertiesFilter;
  protected _openedPopups: OpenedPopup[] = [];
  private _openedLabels: OpenedLabel[] = [];
  private _popupGeneration = 0;

  private $onLayerMouseMove?: (e: MapLayerMouseEvent) => void;
  private $onLayerMouseLeave?: (e: MapLayerMouseEvent) => void;

  constructor(
    map: Map,
    public options: O,
  ) {
    super(map, options);
    this._sourceId = this.options.source
      ? (this.options.source as string)
      : `source-${this._layerId}`;

    this._selectionName = this._layerId + '-highlighted';
    this.$onLayerMouseLeave = this._onLayerMouseLeave.bind(this);
    this.$onLayerMouseMove = this._onLayerMouseMove.bind(this);
    map._onMapClickLayers.push(this);
  }

  async addLayer(options: O): Promise<TLayer> {
    options = this.options = { ...this.options, ...(options || {}) };
    if (options.featureIdName) {
      this.featureIdName = options.featureIdName;
    }

    this.layer = [];
    const types = (this._types = options.type ? [options.type] : this._types);
    if (options.paint) {
      await this._beforeLayerLayer(this._sourceId);
      for (const t of types) {
        const geomType = typeAliasForFilter[t];
        if (!geomType) {
          continue;
        }
        let type = t;
        if (t === 'point') {
          const paintType = this._detectPaintType(options.paint);
          if (paintType === 'icon') {
            type = 'point';
          }
        }
        const layerId = this._getLayerNameFromType(t);
        const geomFilter =
          types.length > 1 ? ['==', '$type', geomType] : undefined;

        await this._addGeomLayer(layerId, type, [
          geomFilter,
          this._getNativeFilter(),
        ]);
        this.layer.push(layerId);

        if (t === 'polygon') {
          const strokeId = `${layerId}-stroke`;
          await this._addGeomLayer(strokeId, 'line', [
            geomFilter,
            this._getNativeFilter(),
          ]);
          this.layer.push(strokeId);
        }
        if (options.selectedPaint) {
          const selFillId = this._getSelectionLayerNameFromType(t);
          await this._addGeomLayer(
            selFillId,
            type,
            [geomFilter, ['in', this.featureIdName, '']],
            this.options.selectedLayout,
          );
          this.layer.push(selFillId);

          if (t === 'polygon') {
            const selStrokeId = `${selFillId}-stroke`;
            await this._addGeomLayer(
              selStrokeId,
              'line',
              [geomFilter, ['in', this.featureIdName, '']],
              this.options.selectedLayout,
            );
            this.layer.push(selStrokeId);
          }
        }
      }
      await this._onLayerAdd(this._sourceId);
    }

    this._addEventsListeners();

    return this.layer;
  }

  async propertiesFilter(
    filters?: PropertiesFilter | null,
    options?: FilterOptions,
  ): Promise<void> {
    this._filterProperties = filters ?? undefined;
    this._updatePropertiesFilter();
  }

  removeFilter(): void {
    this._filterProperties = undefined;
    this._updateFilter();
  }

  select(properties?: DataLayerFilter<F, TLayer> | PropertiesFilter): void {
    if (typeof properties !== 'function') {
      this._selectProperties = properties;
      this._updateFilter();
    }
    this.selected = true;
    const { onSelect, onLayerSelect } = this.options;
    const onSelect_ = onSelect || onLayerSelect;
    if (onSelect_) {
      const features: Feature[] = [];
      onSelect_({
        layer: this,
        features,
        type: 'api',
        ...createFeaturePositionOptions(features),
      });
    }
  }

  unselect(): void {
    this._selectProperties = undefined;
    this._selectedFeatureIds = [];
    this._updateFilter();
    this.selected = false;
    const { onSelect, onLayerSelect } = this.options;
    const onSelect_ = onSelect || onLayerSelect;
    if (onSelect_) {
      onSelect_({
        layer: this,
        features: undefined,
        type: 'api',
        ...createFeaturePositionOptions([]),
      });
    }
    this._removeAllPopups();
    this._removeAllLabels();
  }

  beforeRemove(): void {
    const map = this.map;
    if (map) {
      if (this.layer) {
        this.layer.forEach((layerId) => {
          map.removeLayer(layerId);
        });
      }
      const index = map._onMapClickLayers.indexOf(this);
      if (index !== -1) {
        map._onMapClickLayers.splice(index, 1);
      }
    }
    this._removeAllPopups();
    this._removeAllLabels();
    this._removeEventListeners();
    this.$onLayerMouseLeave = undefined;
    this.$onLayerMouseMove = undefined;
    super.beforeRemove();
  }

  setOpacity(val: number): void {
    this.options.opacity = Number(val);
    if (this.options.type) {
      this._updateLayerPaint(this.options.type);
    }
  }

  setPaint(paint?: Paint | null): void {
    if (paint) {
      this.options.paint = paint;
      if (this.options.type) {
        this._updateLayerPaint(this.options.type);
      }
    }
  }
  setSelectedPaint(paint?: Paint | null): void {
    if (paint) {
      this.options.selectedPaint = paint;
      if (this.options.type) {
        this._updateLayerPaint(this.options.type);
      }
    }
  }
  updatePaint(paint: Partial<Paint>): void {
    this.options.paint = { ...this.options.paint, ...paint } as Paint;
    if (this.options.type) {
      this._updateLayerPaint(this.options.type);
    }
  }
  updateSelectedPaint(paint: Partial<Paint>): void {
    this.options.selectedPaint = {
      ...this.options.selectedPaint,
      ...paint,
    } as Paint;
    if (this.options.type) {
      this._updateLayerPaint(this.options.type);
    }
  }

  _onLayerClick(
    e: MapLayerMouseEvent,
  ): Feature<Geometry, GeoJsonProperties> | undefined {
    e.preventDefault();
    let feature: Feature | undefined;
    const map = this.map;
    if (!map) {
      return;
    }
    if (this.layer) {
      feature = this._getFeatureFromPoint(e);
      if (feature) {
        if (this.options.onClick) {
          this.options.onClick(this._createLayerClickOptions(e, feature));
        }
      }
    }
    return feature;
  }
  _onLayerDoubleClick(
    e: MapLayerMouseEvent,
  ): Feature<Geometry, GeoJsonProperties> | undefined {
    e.preventDefault();
    let feature: Feature | undefined;
    const map = this.map;
    if (!map) {
      return;
    }
    if (this.layer) {
      feature = this._getFeatureFromPoint(e);
      if (feature) {
        if (this.options.onDoubleClick) {
          this.options.onDoubleClick(this._createLayerClickOptions(e, feature));
        }
      }
    }
    return feature;
  }

  protected _resolveRenderedFeature(feature: Feature): Feature {
    return feature;
  }

  protected _createLayerClickOptions(e: MapLayerMouseEvent, f: Feature) {
    const resolvedFeature = this._resolveRenderedFeature(f);

    const isSelected = this._featureSelect(resolvedFeature, e.lngLat);

    const mapFeature = resolvedFeature as MapGeoJSONFeature;
    const geojson = mapFeature.toJSON ? mapFeature.toJSON() : resolvedFeature;
    const feature = {
      ...geojson,
      properties: geojson.properties ? { ...geojson.properties } : null,
    } as Feature;
    return {
      layer: this,
      selected: isSelected,
      event: convertMapClickEvent(e),
      source: e,
      ...this._createLayerOptions(feature),
    };
  }

  protected _featureSelect(
    feature: Feature,
    coordinates?: LngLatLike,
  ): boolean {
    const { selectable, multiselect, unselectOnSecondClick } = this.options;
    const alreadySelected = this.isFeatureSelected(feature);
    let becameSelected = alreadySelected;
    if (selectable || multiselect) {
      let features: Feature[] | undefined = undefined;
      if (alreadySelected) {
        if (unselectOnSecondClick && !this.options.selectOnHover) {
          this._unselectFeature(feature, { silent: true });
          becameSelected = false;
        }
      } else {
        if (!multiselect && this.map) {
          this.map._addUnselectCb(() =>
            this._unselectFeature(feature, { silent: true }),
          );
        }
        features = this._selectFeature(feature, { silent: true });
        becameSelected = true;
      }
      if (this.options.onSelect) {
        this.options.onSelect({
          layer: this,
          features,
          type: 'click',
          ...createFeaturePositionOptions(feature),
        });
      }
    }
    if (becameSelected && this.options.popupOnSelect) {
      this._openPopup({
        coordinates,
        feature,
        options: this.options.popupOptions,
        type: 'click',
        refresh: true,
      });
    }
    this.selected = becameSelected;
    return this.selected;
  }

  protected _updateWithNativeFilter(filter: any[]): any[] {
    const nativeFilter = this._getNativeFilter();
    if (nativeFilter.length) {
      filter.push(nativeFilter);
    }
    return filter;
  }

  protected _getNativeFilter(): PropertyFilter {
    return (
      this.options.nativeFilter ? this.options.nativeFilter : []
    ) as PropertyFilter;
  }

  protected async _addGeomLayer(
    name: string,
    type: VectorAdapterLayerType,
    filter?: any[],
    layout?: Layout,
  ): Promise<void> {
    let mType: MaplibreGLLayerType | undefined;
    if (this.options.paint) {
      if ('type' in this.options.paint && this.options.paint.type === 'icon') {
        mType = 'symbol';
      }
    }
    if (mType === undefined) {
      mType = maplibreGLTypeAlias[type];
    }
    layout = (layout || this.options.layout || {}) as Layout;
    const layerOpt: Layer = {
      id: name,
      type: mType,
      source: this._sourceId,
      filter: filter as FilterSpecification,
      layout: {
        visibility: 'none',
        ...layout,
      },
      ...this._getAdditionalLayerOptions(),
    };
    if (this.options.nativeOptions) {
      Object.assign(layerOpt, this.options.nativeOptions);
    }
    this._addLayer(layerOpt);
  }

  protected _addLayer(layerOpt: Layer): void {
    const { filter, ...opt } = layerOpt;
    const { minZoom, maxZoom } = this.options;
    if (minZoom) {
      opt.minzoom = convertZoomLevel(minZoom);
    }
    if (maxZoom) {
      opt.maxzoom = convertZoomLevel(maxZoom);
    }
    const map = this.map;
    if (map) {
      map.addLayer(opt as LayerSpecification);
      if (filter) {
        const filter_ = (filter || []) as FilterSpecification[];
        const filters = ['all', ...filter_].filter(Boolean);
        map.setFilter(opt.id, filters as FilterSpecification);
      }
    }
  }

  protected async _beforeLayerLayer(
    sourceId: string,
    options?: SourceSpecification,
  ): Promise<void> {
    // ignore
  }
  protected async _onLayerAdd(
    sourceId: string,
    options?: SourceSpecification,
  ): Promise<void> {
    // ignore
  }

  protected async _updateLayerPaint(
    type: VectorAdapterLayerType,
    layers?: [string, Paint][],
  ): Promise<void> {
    const layerName = this._getLayerNameFromType(type);

    if (this.options.paint) {
      if (!layers) {
        layers = [[layerName, this.options.paint]];
        if (this.options.selectedPaint) {
          const selName = this._getSelectionLayerNameFromType(type);
          layers.push([selName, this.options.selectedPaint]);
        }
      }

      const maplibreGLType = maplibreGLTypeAlias[type];
      for (const [name, paint] of layers) {
        let _paint: any = null;
        let nativePaint: Record<string, any> | null = null;
        let typePaint = null;
        if (this.options.nativePaint) {
          nativePaint =
            typeof this.options.nativePaint === 'boolean'
              ? paint
              : this.options.nativePaint;
          const opacity = this.options.opacity;
          delete nativePaint.type;
          if (opacity !== undefined && nativePaint) {
            const allowedNativePaint = allowedByType[type];
            const opacityProp = allowedNativePaint.find(
              (x) => x[0] === 'opacity',
            );
            if (opacityProp) {
              // TODO: check
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              nativePaint[maplibreGLType + '-' + opacityProp[1]];
            }
            for (const p in nativePaint) {
              if (p.indexOf('opacity') !== -1) {
                nativePaint[p] = opacity;
              }
            }
          }
        } else {
          typePaint = await this._createPaintForType(paint, type, name);
        }
        _paint = typePaint || nativePaint;
        if (this.map) {
          if ('icon-image' in _paint) {
            // If true, the icon will be visible even if it collides with other previously drawn symbols.
            _paint['icon-allow-overlap'] = true;
            for (const p in _paint) {
              try {
                this.map.setLayoutProperty(
                  name,
                  p as Parameters<Map['setLayoutProperty']>[1],
                  _paint[p],
                );
              } catch {
                //
              }
            }
          } else {
            for (const p in _paint) {
              this.map.setPaintProperty(
                name,
                p as Parameters<Map['setPaintProperty']>[1],
                _paint[p],
              );
            }
          }
        }
      }
      if (type === 'polygon') {
        layers = [[`${layerName}-stroke`, this.options.paint]];
        if (this.options.selectedPaint) {
          const selName = this._getSelectionLayerNameFromType(type);
          layers.push([`${selName}-stroke`, this.options.selectedPaint]);
        }
        this._updateLayerPaint('line', layers);
      }
    }
  }

  protected _getLayerNameFromType(type: string): string {
    return type + '-' + this._layerId;
  }

  protected _getSelectionLayerNameFromType(
    type: VectorAdapterLayerType,
  ): string {
    return type + '-' + this._selectionName;
  }

  protected async _createPaintForType(
    paint: Paint,
    type: VectorAdapterLayerType,
    name?: string,
  ): Promise<any> {
    if (isPaint(paint)) {
      const maplibreGLPaint: any = {};
      const paint_ = { ...PAINT, ...(paint || {}) } as PathPaint;
      if (paint_.color !== undefined) {
        paint_.fillColor ??= paint_.color;
        paint_.strokeColor ??= paint_.color;
      }
      if (paint.type === 'icon' && paint.html) {
        await this._registerImage(paint);
        const image: Record<string, unknown> = {
          'icon-image': paint.html,
        };
        if (paint.rotate) {
          image['icon-rotate'] = paint.rotate;
        }
        if (paint.iconAnchor) {
          image['icon-anchor'] = 'bottom-right';
          image['icon-offset'] = paint.iconAnchor;
        }
        return image;
      } else {
        const pathPaint = paint_;
        const maplibreGLType = maplibreGLTypeAlias[type];
        const allowed = allowedByType[type];
        if (allowed) {
          let p: keyof typeof pathPaint;
          for (p in pathPaint) {
            // Special case for strokeColor
            const polyStroke = type === 'polygon' && p === 'strokeColor';
            if (polyStroke && pathPaint.stroke) {
              maplibreGLPaint['fill-outline-color'] = pathPaint[p];
            }
            if (!polyStroke) {
              const allowedType = allowed.find((x) => {
                if (typeof x === 'string') {
                  return x === p;
                } else if (Array.isArray(x)) {
                  return x[0] === p;
                }
                return false;
              });
              if (allowedType) {
                if (
                  allowedType[1] &&
                  allowedType[1].includes('stroke') &&
                  !pathPaint.stroke
                ) {
                  continue;
                }
                const paramName = Array.isArray(allowedType)
                  ? allowedType[1]
                  : allowedType;

                const opacity = this.options.opacity;
                let prop = pathPaint[p];
                if (
                  opacity !== undefined &&
                  opacity !== null &&
                  paramName.indexOf('opacity') !== -1
                ) {
                  prop = Number(prop) * opacity;
                }

                maplibreGLPaint[maplibreGLType + '-' + paramName] = prop;
              }
            }
          }
        }
        maplibreGLPaint[maplibreGLType + '-opacity-transition'] = {
          duration: 0,
        };
        return maplibreGLPaint;
      }
    } else if (isPaintCallback(paint) && paint.paint) {
      return this._createPaintForType(paint.paint, type);
    }
  }

  protected _getFeatureFilterId(feature: Feature): string | number | undefined {
    const id = feature._featureFilterId;
    if (id !== undefined) {
      return id;
    } else if (
      feature.properties &&
      feature.properties[this.featureIdName] !== undefined
    ) {
      return feature.properties[this.featureIdName];
    }
    return feature.id;
  }

  protected async _registerImage(paint: IconPaint): Promise<void> {
    if (isIcon(paint) && paint.html && this.map) {
      const imageExist = this.map.hasImage(paint.html);
      if (!imageExist) {
        let width = 12;
        let height = 12;
        if (paint.iconSize) {
          width = paint.iconSize[0];
          height = paint.iconSize[1];
        }
        const image = await getImage(paint.html, {
          width,
          height,
        });
        if (this.map) {
          this.map.addImage(paint.html, image);
        }
      }
    }
  }

  protected _setFeatureIdsSelected(feature: Feature | Feature[]) {
    let selectedFeatureIds = this._selectedFeatureIds || [];
    if (this.options && !this.options.multiselect) {
      selectedFeatureIds = [];
    }
    let features: Feature[] = [];
    if (Array.isArray(feature)) {
      features = feature;
    } else {
      features = [feature];
    }
    features.forEach((f) => {
      const id = this._getFeatureFilterId(f);
      if (id !== undefined) {
        selectedFeatureIds.push(id);
      }
    });
    this._selectProperties = undefined;
    this._selectedFeatureIds = selectedFeatureIds;
    return features;
  }

  protected _selectFeature(
    feature: Feature | Feature[],
    opt?: { silent: boolean },
  ): Feature[] {
    const features = this._setFeatureIdsSelected(feature);
    this.select([
      [
        this.featureIdName,
        'in',
        features.map(
          (x) => (x.properties && x.properties[this.featureIdName]) || x.id,
        ),
      ],
    ]);
    return features;
  }

  protected _unselectFeature(
    feature?: Feature | Feature[],
    opt?: EventOptions,
  ): void {
    if (feature) {
      let features: Feature[] = [];
      if (Array.isArray(feature)) {
        features = feature;
      } else {
        features = [feature];
      }
      if (features.length) {
        for (const f of features) {
          const id = this._getFeatureFilterId(f);
          const selected = this._selectedFeatureIds;
          if (selected && id !== undefined) {
            const index = selected.indexOf(id);
            if (index !== -1) {
              selected.splice(index, 1);
            }
          }
          this._removeFeaturePopup(f, true);
        }
      }
    } else {
      this._selectedFeatureIds = false;
    }
    this._selectProperties = undefined;
    this._updateFilter(opt);
  }

  protected _getAdditionalLayerOptions(): Record<string, unknown> {
    return {};
  }

  protected _updateFilter(opt?: EventOptions): void {
    this._updatePropertiesFilter();
  }

  protected _updatePropertiesFilter(): void {
    const layers = this.layer;
    const map = this.map;
    if (layers) {
      for (const t of this._types) {
        const geomType = typeAliasForFilter[t];
        if (geomType) {
          const geomFilter: LegacyFilterSpecification = [
            '==',
            '$type',
            geomType,
          ];
          const layerName = this._getLayerNameFromType(t);
          const layerNameStroke = `${layerName}-stroke`;
          const selLayerName = this._getSelectionLayerNameFromType(t);
          const selLayerNameStroke = `${selLayerName}-stroke`;
          const selectProperties = this._selectProperties;
          const filterProperties = this._filterProperties;
          const propertyFilters =
            filterProperties &&
            this._convertToMaplibreGLFilter(filterProperties);
          if (map) {
            [selLayerName, selLayerNameStroke].forEach((name) => {
              if (layers.indexOf(name) !== -1) {
                if (this._selectionName) {
                  let filters: any[] = [];
                  if (selectProperties || this._selectedFeatureIds) {
                    if (selectProperties) {
                      filters =
                        this._convertToMaplibreGLFilter(selectProperties) || [];
                    } else if (this._selectedFeatureIds) {
                      filters = [
                        ['in', this.featureIdName, ...this._selectedFeatureIds],
                      ];
                    }
                    if (propertyFilters) {
                      propertyFilters.forEach((x) => filters.push(x));
                    }
                    map.setFilter(name, ['all', geomFilter, ...filters]);
                  } else {
                    filters = ['in', '$id', ''];
                    map.setFilter(name, filters as FilterSpecification);
                  }
                }
              }
            });
            [layerName, layerNameStroke].forEach((name) => {
              if (layers.indexOf(name) !== -1) {
                const filters_: any[] = ['all', geomFilter];
                this._updateWithNativeFilter(filters_);
                if (this.options.selectedPaint) {
                  if (selectProperties) {
                    const selectFilters = this._convertToMaplibreGLFilter(
                      selectProperties,
                      true,
                    );
                    selectFilters.forEach((x) => filters_.push(x));
                  } else if (this._selectedFeatureIds) {
                    filters_.push([
                      '!in',
                      this.featureIdName,
                      ...this._selectedFeatureIds,
                    ]);
                  }
                }
                if (propertyFilters) {
                  propertyFilters.forEach((x) => filters_.push(x));
                }
                map.setFilter(name, filters_ as FilterSpecification);
              }
            });
          }
        }
      }
    }
  }

  protected _convertToMaplibreGLFilter(
    filters: PropertiesFilter,
    reverse = false,
  ): (any[] | 'all' | 'any' | undefined)[] {
    const _operationsAliases = reverse ? reversOperations : operationsAliases;
    const filter = filters.map((x) => {
      if (typeof x === 'string') {
        return x;
      } else if (isPropertyFilter(x)) {
        const [field, operation, value] = x;
        const operationAlias = _operationsAliases[operation];
        if (operation === 'in' || operation === 'notin') {
          return [operationAlias, field, ...value];
        }
        return [operationAlias, field, value];
      }
    });
    return filter;
  }

  protected isFeatureSelected(feature: Feature): boolean {
    if (this._selectedFeatureIds && this._selectedFeatureIds.length) {
      const filterId = this._getFeatureFilterId(feature);
      if (filterId) {
        return this._selectedFeatureIds.indexOf(filterId) !== -1;
      }
    } else if (this._selectProperties && this._selectProperties.length) {
      return featureFilter(feature, this._selectProperties);
    }
    return false;
  }

  protected _removeAllPopups(): void {
    this._popupGeneration++;
    const popups = this._openedPopups.map(({ popup }) => popup);
    for (const popup of popups) {
      this._removePopup(popup, true);
    }
  }

  protected async _openPopup({
    coordinates,
    feature,
    options = {},
    type,
    refresh,
  }: {
    coordinates?: LngLatLike;
    feature: Feature;
    options?: PopupOptions;
    type: OnLayerSelectType;
    refresh?: boolean;
  }): Promise<void> {
    if (refresh) {
      const openedPopup = this._findOpenedPopup(feature);
      if (openedPopup) {
        if (coordinates) {
          openedPopup.popup.setLngLat(coordinates);
        }
        return;
      }
    }
    if (!this.options.multiselect) {
      this._removeAllPopups();
    }
    const popupGeneration = this._popupGeneration;
    const map = this.map;
    if (!map) return;
    let popup: Popup;
    const _closeHandlers: PopupOnCloseFunction[] = [];
    const onClose = (handler: PopupOnCloseFunction) => {
      _closeHandlers.push(handler);
    };
    const close = () => {
      if (popup) {
        this._removePopup(popup);
      }
    };
    const {
      maxWidth,
      popupContent,
      createPopupContent,
      closeButton: closeBtn,
    } = options;
    const closeButton = closeBtn ?? !this.options.selectOnHover;

    const content = createPopupContent
      ? await createPopupContent({
          type,
          close,
          onClose,
          ...this._createLayerOptions(feature),
        })
      : popupContent;
    if (popupGeneration !== this._popupGeneration) {
      return;
    }
    if (refresh) {
      const openedPopup = this._findOpenedPopup(feature);
      if (openedPopup) {
        if (coordinates) {
          openedPopup.popup.setLngLat(coordinates);
        }
        return;
      }
    }
    coordinates =
      coordinates || (feature && (getCentroid(feature) as [number, number]));
    if (content && coordinates) {
      const html =
        typeof content === 'string' ? makeHtmlFromString(content) : content;
      const popupOpt: MaplibrePopupOptions = {
        closeButton,
        closeOnClick: false,
      };
      if (maxWidth) {
        popupOpt.maxWidth = typeof maxWidth === 'number' ? maxWidth + 'px' : '';
      }
      popup = new Popup(popupOpt);
      this._openedPopups.push({
        feature,
        popup,
        closeHandlers: _closeHandlers,
      });
      popup.once('close', () => this._removePopup(popup, false, false));
      popup.setLngLat(coordinates).setDOMContent(html).addTo(map);
    }
  }

  protected _openLabel(f: Feature, lngLat?: [number, number]): void {
    const map = this.map;
    const { labelField, label } = this.options;
    if (map && (labelField || label)) {
      const popupOpt: MaplibrePopupOptions = {
        closeButton: false,
        closeOnClick: false,
        closeOnMove: this.options.labelOnHover,
      };
      let text: string | undefined;
      if (labelField) {
        text = f.properties && f.properties[labelField];
      } else if (label) {
        text = label(this._createLayerOptions(f));
      }
      if (text) {
        const isOpened = this._openedLabels.some(({ feature }) =>
          this._isSameFeature(feature, f),
        );
        if (!isOpened) {
          const popup = new Popup(popupOpt);
          const openedLabel = { feature: f, popup };
          this._openedLabels.push(openedLabel);
          popup.once('close', () => {
            const index = this._openedLabels.indexOf(openedLabel);
            if (index !== -1) {
              this._openedLabels.splice(index, 1);
            }
          });
          lngLat = lngLat ?? (getCentroid(f) as [number, number]);
          popup.setLngLat(lngLat).setText(text).addTo(map);
        }
      }
    }
  }

  protected _removeAllLabels(): void {
    const labels = this._openedLabels.splice(0);
    for (const { popup } of labels) {
      popup.remove();
    }
  }

  protected _removeFeaturePopup(feature: Feature, doNotUnselect = false): void {
    const openedPopup = this._findOpenedPopup(feature);
    if (openedPopup) {
      this._removePopup(openedPopup.popup, doNotUnselect);
    }
  }

  protected _createLayerOptions(feature: Feature): LayerDefinition {
    return {
      target: this,
      feature,
      ...createFeaturePositionOptions(feature),
    };
  }

  private _isSameFeature(a: Feature, b: Feature): boolean {
    const aId = this._getFeatureFilterId(a);
    const bId = this._getFeatureFilterId(b);
    return aId !== undefined && bId !== undefined ? aId === bId : a === b;
  }

  private _findOpenedPopup(feature: Feature): OpenedPopup | undefined {
    return this._openedPopups.find((opened) =>
      this._isSameFeature(opened.feature, feature),
    );
  }

  private _removePopup(
    popup: Popup,
    doNotUnselect = false,
    removeNative = true,
  ): void {
    const index = this._openedPopups.findIndex(
      (opened) => opened.popup === popup,
    );
    if (index === -1) {
      return;
    }

    const [opened] = this._openedPopups.splice(index, 1);
    if (removeNative) {
      popup.remove();
    }
    for (const handler of opened.closeHandlers) {
      handler(this._createLayerOptions(opened.feature));
    }
    if (
      (this.options.popupOptions?.unselectOnClose ?? true) &&
      !doNotUnselect
    ) {
      this._unselectFeature(opened.feature);
    }
  }

  private _getFeatureFromPoint(
    evt: MapEventType['click'] & MapMouseEvent,
  ): Feature | undefined {
    const map = this.map;
    if (map && this.options.onMouseOver && this.layer) {
      const features = map.queryRenderedFeatures(evt.point, {
        layers: this.layer,
      });
      return features[0] as Feature | undefined;
    }
  }

  private _onLayerMouseMove(evt: MapEventType['mousemove'] & MapMouseEvent) {
    const map = this.map;
    if (map) {
      const {
        onMouseOver,
        selectOnHover,
        selectable,
        interactive,
        labelOnHover,
        popupOnHover,
      } = this.options;
      const event = convertMapClickEvent(evt);
      if (
        onMouseOver ||
        selectOnHover ||
        interactive ||
        labelOnHover ||
        popupOnHover
      ) {
        const feature = this._getFeatureFromPoint(evt);
        if (onMouseOver && this.layer) {
          onMouseOver({
            event,
            layer: this,
            source: evt,
            feature,
            ...createFeaturePositionOptions(feature || []),
          });
        }
        if (feature) {
          if (selectOnHover) {
            this._featureSelect(feature);
          }
          if (labelOnHover) {
            this._removeAllLabels();
            this._openLabel(feature, event.lngLat as [number, number]);
          } else if (popupOnHover) {
            this._removeAllPopups();
            this._openPopup({
              feature,
              type: 'api',
              options: this.options.popupOptions,
            });
          }
        }
      }
      if (selectable || interactive) {
        map.getCanvas().style.cursor = 'pointer';
      }
    }
  }

  private _onLayerMouseLeave(evt: MapEventType['mousemove'] & MapMouseEvent) {
    const { onMouseOut, labelOnHover, popupOnHover, selectOnHover } =
      this.options;
    if (this.map) {
      if (onMouseOut) {
        onMouseOut({
          event: convertMapClickEvent(evt),
          layer: this,
          source: evt,
        });
      }
      this.map.getCanvas().style.cursor = '';
    }
    if (selectOnHover) {
      this.unselect();
    }
    if (labelOnHover) {
      this._removeAllLabels();
    }
    if (popupOnHover) {
      this._removeAllPopups();
    }
  }

  private _detectPaintType(paint: Paint): string | undefined {
    if ('type' in paint) {
      return paint.type;
    } else if (typeof paint === 'function') {
      try {
        const falsePaint = paint({
          type: 'Feature',
          properties: {},
          geometry: {} as Geometry,
        });
        return this._detectPaintType(falsePaint);
      } catch {
        //
      }
    }
  }

  private _addEventsListeners() {
    if (this.layer && this.options) {
      this.layer.forEach((x) => {
        if (this.$onLayerMouseMove && this.map) {
          this.map.on('mousemove', x, this.$onLayerMouseMove);
        }
        if (this.$onLayerMouseLeave && this.map) {
          this.map.on('mouseleave', x, this.$onLayerMouseLeave);
        }
      });
    }
  }

  private _removeEventListeners() {
    if (this.layer && this.map) {
      this.layer.forEach((x) => {
        if (this.$onLayerMouseMove && this.map) {
          this.map.off('mousemove', x, this.$onLayerMouseMove);
        }
        if (this.$onLayerMouseLeave && this.map) {
          this.map.off('mouseleave', x, this.$onLayerMouseLeave);
        }
      });
    }
  }
}
